import React, { useEffect, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  FormControlLabel,
  Grid,
  InputLabel,
  MenuItem,
  Select,
  Switch,
  Tab,
  Tabs,
  TextField,
  Typography,
} from "@mui/material";
import type { StarterEntry } from "../../ops";
import apiService from "../../../services/api";
import {
  EMPTY_MARKETING_TEMPLATE_FORM,
  formFromApiRow,
  formFromStarter,
  MARKETING_TEMPLATE_CATEGORIES,
  type MarketingTemplateForm,
  toApiPayload,
} from "./marketingTemplateUtils";

export type EditorMode = "create" | "edit";

export interface MarketingTemplateEditorDialogProps {
  open: boolean;
  mode: EditorMode;
  templateId?: string | null;
  starter?: StarterEntry | null;
  initialTab?: number;
  onClose: () => void;
  onSaved: () => void;
}

export const MarketingTemplateEditorDialog: React.FC<
  MarketingTemplateEditorDialogProps
> = ({
  open,
  mode,
  templateId,
  starter,
  initialTab = 0,
  onClose,
  onSaved,
}) => {
  const [tab, setTab] = useState(initialTab);
  const [form, setForm] = useState<MarketingTemplateForm>(
    EMPTY_MARKETING_TEMPLATE_FORM,
  );
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!open) return;
    setTab(initialTab);
    setError(null);

    if (mode === "edit" && templateId) {
      setLoading(true);
      (async () => {
        try {
          const res: any = await apiService.get(
            `/api/admin/email-templates/${templateId}`,
          );
          const row = res?.data ?? res;
          setForm(formFromApiRow(row));
        } catch (e: any) {
          setError(e?.message ?? "Failed to load template");
        } finally {
          setLoading(false);
        }
      })();
      return;
    }

    if (starter) {
      setForm(formFromStarter(starter));
    } else {
      setForm({ ...EMPTY_MARKETING_TEMPLATE_FORM });
    }
  }, [open, mode, templateId, starter, initialTab]);

  const handleSave = async () => {
    setError(null);
    if (!form.template_key.trim() || form.template_key.length < 3) {
      setError("Template key must be at least 3 characters.");
      return;
    }
    if (!form.template_name.trim()) {
      setError("Template name is required.");
      return;
    }
    if (!form.subject_line.trim()) {
      setError("Subject line is required.");
      return;
    }
    if (!form.html_content.trim()) {
      setError("HTML content is required.");
      return;
    }

    setSaving(true);
    try {
      const payload = toApiPayload(form);
      if (mode === "edit" && templateId) {
        await apiService.put(`/api/admin/email-templates/${templateId}`, payload);
      } else {
        await apiService.post("/api/admin/email-templates", payload);
      }
      onSaved();
      onClose();
    } catch (e: any) {
      const msg =
        e?.message ||
        (typeof e === "string" ? e : null) ||
        "Failed to save template";
      setError(msg.includes("HTTP") ? "Failed to save template. Check category and template key." : msg);
    } finally {
      setSaving(false);
    }
  };

  const title =
    mode === "edit"
      ? `Edit template — ${form.template_name || form.template_key}`
      : starter
        ? `New template — ${starter.name}`
        : "New marketing template";

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle sx={{ fontFamily: '"Playfair Display", serif', fontWeight: 700 }}>
        {title}
      </DialogTitle>
      <DialogContent dividers>
        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}
        {loading ? (
          <Typography color="text.secondary">Loading template…</Typography>
        ) : (
          <>
            <Tabs value={tab} onChange={(_, v) => setTab(v)} sx={{ mb: 2 }}>
              <Tab label="Basic" />
              <Tab label="Content" />
              <Tab label="Preview" />
            </Tabs>

            {tab === 0 && (
              <Grid container spacing={2}>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    required
                    label="Template key"
                    value={form.template_key}
                    disabled={mode === "edit"}
                    onChange={(e) =>
                      setForm({ ...form, template_key: e.target.value })
                    }
                    helperText="Unique ID, e.g. launch50_promo"
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    required
                    label="Template name"
                    value={form.template_name}
                    onChange={(e) =>
                      setForm({ ...form, template_name: e.target.value })
                    }
                  />
                </Grid>
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    label="Description"
                    value={form.description}
                    onChange={(e) =>
                      setForm({ ...form, description: e.target.value })
                    }
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <FormControl fullWidth>
                    <InputLabel>Category</InputLabel>
                    <Select
                      label="Category"
                      value={form.category}
                      onChange={(e) =>
                        setForm({ ...form, category: e.target.value })
                      }
                    >
                      {MARKETING_TEMPLATE_CATEGORIES.map((c) => (
                        <MenuItem key={c.id} value={c.id}>
                          {c.label}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    label="Preview text"
                    value={form.preview_text}
                    onChange={(e) =>
                      setForm({ ...form, preview_text: e.target.value })
                    }
                  />
                </Grid>
                <Grid item xs={12} sm={4}>
                  <TextField
                    fullWidth
                    label="From name"
                    value={form.from_name}
                    onChange={(e) =>
                      setForm({ ...form, from_name: e.target.value })
                    }
                  />
                </Grid>
                <Grid item xs={12} sm={4}>
                  <TextField
                    fullWidth
                    label="From email"
                    value={form.from_email}
                    onChange={(e) =>
                      setForm({ ...form, from_email: e.target.value })
                    }
                  />
                </Grid>
                <Grid item xs={12} sm={4}>
                  <FormControl fullWidth>
                    <InputLabel>Status</InputLabel>
                    <Select
                      label="Status"
                      value={form.status}
                      onChange={(e) =>
                        setForm({
                          ...form,
                          status: e.target.value as MarketingTemplateForm["status"],
                        })
                      }
                    >
                      <MenuItem value="draft">Draft</MenuItem>
                      <MenuItem value="active">Active</MenuItem>
                      <MenuItem value="inactive">Inactive</MenuItem>
                    </Select>
                  </FormControl>
                </Grid>
                <Grid item xs={12}>
                  <FormControlLabel
                    control={
                      <Switch
                        checked={form.is_active}
                        onChange={(e) =>
                          setForm({ ...form, is_active: e.target.checked })
                        }
                      />
                    }
                    label="Active (can be used in campaigns)"
                  />
                </Grid>
              </Grid>
            )}

            {tab === 1 && (
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                <TextField
                  fullWidth
                  required
                  label="Subject line"
                  value={form.subject_line}
                  onChange={(e) =>
                    setForm({ ...form, subject_line: e.target.value })
                  }
                  helperText="Use {{user_name}} and other merge tags"
                />
                <TextField
                  fullWidth
                  required
                  label="HTML content"
                  value={form.html_content}
                  onChange={(e) =>
                    setForm({ ...form, html_content: e.target.value })
                  }
                  multiline
                  minRows={12}
                />
                <TextField
                  fullWidth
                  label="Plain text"
                  value={form.plain_text_content}
                  onChange={(e) =>
                    setForm({ ...form, plain_text_content: e.target.value })
                  }
                  multiline
                  minRows={4}
                />
              </Box>
            )}

            {tab === 2 && (
              <Box>
                <Typography variant="caption" color="text.secondary">
                  Subject
                </Typography>
                <Typography variant="subtitle2" sx={{ mb: 2, fontFamily: "monospace" }}>
                  {form.subject_line || "(no subject)"}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  HTML preview
                </Typography>
                <Box
                  sx={{
                    mt: 1,
                    border: "1px solid",
                    borderColor: "divider",
                    borderRadius: 1,
                    p: 2,
                    bgcolor: "#fff",
                    minHeight: 200,
                    overflow: "auto",
                  }}
                  dangerouslySetInnerHTML={{
                    __html: form.html_content || "<p><em>No HTML yet</em></p>",
                  }}
                />
              </Box>
            )}
          </>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={saving}>
          Cancel
        </Button>
        <Button
          variant="contained"
          onClick={handleSave}
          disabled={saving || loading}
        >
          {saving ? "Saving…" : mode === "edit" ? "Save changes" : "Create template"}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default MarketingTemplateEditorDialog;
